
import { useEventFetch } from "./use-event-fetch";
import { useEventUpdate } from "./use-event-update";
import { useAttendeeRemoval } from "./use-attendee-removal";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";

export const useEventDetails = (eventId: string) => {
  const { data: event, isLoading, refetch } = useEventFetch(eventId);
  const updateMutation = useEventUpdate(eventId, refetch);
  const { removeGuest, isRemoving } = useAttendeeRemoval(eventId);
  
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [rsvpStatus, setRsvpStatus] = useState<'invited' | 'accepted' | 'declined' | 'maybe' | undefined>(undefined);
  const [isHost, setIsHost] = useState(false);

  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUserId(user?.id || null);
    };
    getCurrentUser();
  }, []);

  useEffect(() => {
    if (event && currentUserId) {
      // Check if the current user is the host
      const userIsHost = event.host_user_id === currentUserId;
      setIsHost(userIsHost);

      // If not host, check if they're a guest and get their RSVP status
      if (!userIsHost) {
        // First check in attendees from event_attendee table (older implementation)
        if (event.attendees) {
          const currentUserAttendee = event.attendees.find(
            (attendee: any) => attendee.user_id === currentUserId
          );
          
          if (currentUserAttendee) {
            setRsvpStatus(currentUserAttendee.rsvp_status as any);
            return;
          }
        }

        // If not found in attendees, check event_invites table
        const checkEventInvites = async () => {
          // First try to find by user ID if the invite has been claimed
          const { data: inviteData, error: inviteError } = await supabase
            .from('event_invites')
            .select('rsvp_status')
            .eq('event_id', eventId)
            .eq('claimed_by_user_id', currentUserId)
            .maybeSingle();

          if (inviteError) {
            console.error('Error fetching invite data:', inviteError);
            return;
          }

          if (inviteData) {
            setRsvpStatus(inviteData.rsvp_status as any);
            return;
          }

          // If not found by user ID, try to match by phone number
          // Get the user's phone number
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('phone_number')
            .eq('user_id', currentUserId)
            .single();

          if (userError || !userData?.phone_number) {
            console.error('Error fetching user data or no phone number:', userError);
            return;
          }

          // Look for invites with the same phone number
          const { data: phoneInviteData, error: phoneInviteError } = await supabase
            .from('event_invites')
            .select('rsvp_status')
            .eq('event_id', eventId)
            .eq('phone_number', userData.phone_number)
            .maybeSingle();

          if (phoneInviteError) {
            console.error('Error fetching invite by phone:', phoneInviteError);
            return;
          }

          if (phoneInviteData) {
            setRsvpStatus(phoneInviteData.rsvp_status as any);
          }
        };

        checkEventInvites();
      }
    }
  }, [event, currentUserId, eventId]);

  return {
    event,
    isLoading,
    updateMutation,
    removeGuest,
    isRemoving,
    currentUserId,
    isHost,
    rsvpStatus,
    refetchEvent: refetch
  };
};
